

import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import GalleriesManager from "../components/GalleriesManager";
import TestimonialsManager from "../components/TestimonialsManager";
import FeaturesManager from "../components/FeaturesManager";
import FAQManager from "../components/FAQManager";
import ReelsManager from "../components/ReelsManager";
import { useToast } from "@/context/ToastContext";
import { imageApi, MAIN_URL } from "@/lib/api";
import { getImageUrl } from "@/lib/utils";
import {
  LayoutTemplate,
  BarChart3,
  Mail,
  Share2,
  FileText,
  Image as ImageIcon,
  Trash2,
  Save,
  Loader2,
  Plus,
  AlertCircle,
  Check,
  Quote,
  Sparkles,
  HelpCircle,
  Video,
} from "lucide-react";

interface SiteSettings {
  hero_title: string;
  hero_subtitle: string;
  hero_images: string[];
  stats_capacity: string;
  stats_events: string;
  stats_experience: string;
  stats_satisfaction: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  social_facebook: string;
  social_instagram: string;
  social_twitter: string;
  footer_about: string;
  // About Page
  about_title: string;
  about_subtitle: string;
  about_content: string;
  about_team: any[];
  about_values: any[];
  about_image: string;
  // Contact Page
  contact_title: string;
  contact_subtitle: string;
  contact_map_embed: string;
  contact_image: string;
  // Inquiry Page
  inquiry_title: string;
  inquiry_subtitle: string;
  inquiry_description: string;
  // Gallery Page
  gallery_title: string;
  gallery_subtitle: string;
  gallery_cta_title: string;
  gallery_cta_subtitle: string;
  gallery_cta_button: string;
  gallery_show_filters: boolean;
}

const defaultSettings: SiteSettings = {
  hero_title: "",
  hero_subtitle: "",
  hero_images: [],
  stats_capacity: "",
  stats_events: "",
  stats_experience: "",
  stats_satisfaction: "",
  contact_email: "",
  contact_phone: "",
  contact_address: "",
  social_facebook: "",
  social_instagram: "",
  social_twitter: "",
  footer_about: "",
  // About Page
  about_title: "",
  about_subtitle: "",
  about_content: "",
  about_team: [],
  about_values: [],
  about_image: "",
  // Contact Page
  contact_title: "",
  contact_subtitle: "",
  contact_map_embed: "",
  contact_image: "",
  // Inquiry Page
  inquiry_title: "",
  inquiry_subtitle: "",
  inquiry_description: "",
  // Gallery Page
  gallery_title: "",
  gallery_subtitle: "",
  gallery_cta_title: "",
  gallery_cta_subtitle: "",
  gallery_cta_button: "",
  gallery_show_filters: true,
};

// API client for site settings
const siteSettingsApi = {
  get: async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${MAIN_URL}/api/site-settings`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch settings');
    return response.json();
  },
  update: async (data: Partial<SiteSettings>) => {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${MAIN_URL}/api/site-settings`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    // Try to parse response even if not OK to get error details
    let responseData;
    try {
      responseData = await response.json();
    } catch (e) {
      responseData = null;
    }
    
    if (!response.ok) {
      const errorMessage = responseData?.message || responseData?.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }
    
    return responseData;
  },
  deleteHeroImage: async (url: string) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${MAIN_URL}/api/site-settings/hero-image`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
    });
    if (!response.ok) throw new Error('Failed to delete image');
    return response.json();
  },
};

// API client for team members
const teamMembersApi = {
  getAll: async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${MAIN_URL}/api/team-members`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch team members');
    return response.json();
  },
  create: async (data: any) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${MAIN_URL}/api/team-members`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create team member');
    return response.json();
  },
  update: async (id: number, data: any) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${MAIN_URL}/api/team-members/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update team member');
    return response.json();
  },
  delete: async (id: number) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${MAIN_URL}/api/team-members/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to delete team member');
    return response.json();
  },
  uploadImage: async (id: number, file: File) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('image', file);
    const response = await fetch(`${MAIN_URL}/api/team-members/${id}/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
      body: formData,
    });
    if (!response.ok) throw new Error('Failed to upload image');
    return response.json();
  },
};

export default function FrontendSettingsPage() {
  const { showToast } = useToast();
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"hero" | "stats" | "contact" | "social" | "footer" | "about" | "contactpage" | "inquiry" | "gallerypage" | "gallery" | "testimonials" | "features" | "faq" | "reels">("hero");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [editingMember, setEditingMember] = useState<any>(null);
  const [memberFormData, setMemberFormData] = useState({
    name: "",
    position: "",
    bio: "",
    image: "",
  });

  useEffect(() => {
    loadSettings();
    loadTeamMembers();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await siteSettingsApi.get();
      if (response.data) {
        // Filter out null values from API response to prevent controlled input warnings
        const filteredData = Object.fromEntries(
          Object.entries(response.data).filter(([_, value]) => value !== null)
        );
        setSettings({
          ...defaultSettings,
          ...filteredData,
          hero_images: response.data.hero_images || [],
        });
      }
    } catch (error) {
      showToast("Failed to load site settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (sectionData: Partial<SiteSettings>) => {
    setSaving(true);
    try {
      await siteSettingsApi.update(sectionData);
      setSettings(prev => ({ ...prev, ...sectionData }));
      showToast("Settings saved successfully", "success");
    } catch (error) {
      showToast("Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const result = await imageApi.upload(file, 'gallery');
      const newUrl = result.data?.url || result.url;
      if (newUrl) {
        const updatedImages = [...settings.hero_images, newUrl];
        await siteSettingsApi.update({ hero_images: updatedImages });
        setSettings(prev => ({ ...prev, hero_images: updatedImages }));
        showToast("Image uploaded successfully", "success");
      }
    } catch (error) {
      showToast("Failed to upload image", "error");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (url: string) => {
    try {
      await siteSettingsApi.deleteHeroImage(url);
      const updatedImages = settings.hero_images.filter(img => img !== url);
      setSettings(prev => ({ ...prev, hero_images: updatedImages }));
      showToast("Image deleted successfully", "success");
    } catch (error) {
      showToast("Failed to delete image", "error");
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await teamMembersApi.getAll();
      if (response.data) {
        setTeamMembers(response.data);
      }
    } catch (error) {
      showToast("Failed to load team members", "error");
    }
  };

  // Dummy Data Functions
  const loadAboutDummyData = async () => {
    try {
      const dummyData = {
        about_title: "About Shalimar Palace",
        about_subtitle: "Creating Unforgettable Memories Since 1995",
        about_content: "Founded in 1995, Shalimar Palace & Resort has been the premier destination for celebrations in the region. Over the past two and a half decades, we've had the privilege of hosting thousands of events - from intimate family gatherings to magnificent grand weddings.\n\nOur state-of-the-art facilities span over 50,000 square feet, featuring multiple banquet halls, outdoor lawns, and luxury accommodations. We pride ourselves on delivering exceptional service, culinary excellence, and attention to detail that transforms your special occasions into cherished memories.\n\nWith a dedicated team of event planners, chefs, and hospitality professionals, we ensure every celebration is executed flawlessly. Our commitment to innovation, combined with traditional values, makes us the preferred choice for discerning clients.",
        about_image: "https://images.unsplash.com/photo-1519167758481-83f29da8c8b7?w=800&h=600&fit=crop&q=80",
      };
      
      await handleSave(dummyData);
      showToast("About page dummy data loaded successfully", "success");
    } catch (error) {
      showToast("Failed to load dummy data", "error");
    }
  };

  const loadContactDummyData = async () => {
    try {
      const dummyData = {
        contact_title: "Get in Touch",
        contact_subtitle: "We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
        contact_map_embed: '<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.6667890123456!2d77.20902123456789!3d28.613939123456789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDM2JzUwLjIiTiA3N8KwMTInMzIuNSJF!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin" width="100%" height="450" style="border:0;" allowfullscreen="" loading="lazy"></iframe>',
      };
      
      await handleSave(dummyData);
      showToast("Contact page dummy data loaded successfully", "success");
    } catch (error) {
      showToast("Failed to load dummy data", "error");
    }
  };

  const loadInquiryDummyData = async () => {
    try {
      const dummyData = {
        inquiry_title: "Book Your Event",
        inquiry_subtitle: "Start planning your dream celebration with us",
        inquiry_description: "Fill out the form below with your event details, and our experienced team will get back to you within 24 hours with a customized proposal. Whether you're planning an intimate gathering or a grand celebration, we're here to make your vision come to life.",
      };
      
      await handleSave(dummyData);
      showToast("Inquiry page dummy data loaded successfully", "success");
    } catch (error) {
      showToast("Failed to load dummy data", "error");
    }
  };

  const handleContactImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check authentication first
    const token = localStorage.getItem('token');
    if (!token) {
      showToast("Authentication required. Please log in again.", "error");
      return;
    }

    setUploadingImage(true);
    try {
      // Step 1: Upload image to server
      const result = await imageApi.upload(file, 'gallery');
      
      // Extract URL from response - handle different response formats
      const newUrl = result?.data?.url || result?.url || result;
      
      if (!newUrl) {
        throw new Error('No URL returned from upload');
      }

      // Step 2: Update site settings with the new image URL
      const response = await siteSettingsApi.update({ contact_image: newUrl });
      
      // Step 3: Update local state
      setSettings(prev => ({ ...prev, contact_image: newUrl }));
      
      showToast("Contact image uploaded successfully", "success");
    } catch (error) {
      console.error('Contact image upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Check for authentication errors
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        showToast("Session expired. Please log in again.", "error");
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        showToast(`Failed to upload image: ${errorMessage}`, "error");
      }
    } finally {
      setUploadingImage(false);
      // Reset file input to allow re-uploading same file
      e.target.value = '';
    }
  };

  const handleAboutImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check authentication first
    const token = localStorage.getItem('token');
    if (!token) {
      showToast("Authentication required. Please log in again.", "error");
      return;
    }

    setUploadingImage(true);
    try {
      // Step 1: Upload image to server
      const result = await imageApi.upload(file, 'gallery');
      
      // Extract URL from response - handle different response formats
      const newUrl = result?.data?.url || result?.url || result;
      
      if (!newUrl) {
        throw new Error('No URL returned from upload');
      }

      // Step 2: Update site settings with the new image URL
      const response = await siteSettingsApi.update({ about_image: newUrl });
      
      // Step 3: Update local state
      setSettings(prev => ({ ...prev, about_image: newUrl }));
      
      showToast("About page image uploaded successfully", "success");
    } catch (error) {
      console.error('About image upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Check for authentication errors
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        showToast("Session expired. Please log in again.", "error");
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        showToast(`Failed to upload image: ${errorMessage}`, "error");
      }
    } finally {
      setUploadingImage(false);
      // Reset file input to allow re-uploading same file
      e.target.value = '';
    }
  };

  // Team Member Management Functions
  const handleAddTeamMember = async () => {
    if (!memberFormData.name || !memberFormData.position) {
      showToast("Name and position are required", "error");
      return;
    }

    try {
      const response = await teamMembersApi.create({
        ...memberFormData,
        is_active: true,
        sort_order: teamMembers.length,
      });
      
      if (response.data) {
        setTeamMembers([...teamMembers, response.data]);
        setMemberFormData({ name: "", position: "", bio: "", image: "" });
        setEditingMember(null);
        showToast("Team member added successfully", "success");
      }
    } catch (error) {
      showToast("Failed to add team member", "error");
    }
  };

  const handleUpdateTeamMember = async (id: number) => {
    try {
      const response = await teamMembersApi.update(id, memberFormData);
      if (response.data) {
        setTeamMembers(teamMembers.map(m => m.id === id ? response.data : m));
        setMemberFormData({ name: "", position: "", bio: "", image: "" });
        setEditingMember(null);
        showToast("Team member updated successfully", "success");
      }
    } catch (error) {
      showToast("Failed to update team member", "error");
    }
  };

  const handleDeleteTeamMember = async (id: number) => {
    if (!confirm("Are you sure you want to delete this team member?")) return;
    
    try {
      await teamMembersApi.delete(id);
      setTeamMembers(teamMembers.filter(m => m.id !== id));
      showToast("Team member deleted successfully", "success");
    } catch (error) {
      showToast("Failed to delete team member", "error");
    }
  };

  const handleMemberImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, memberId?: number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      if (memberId) {
        const result = await teamMembersApi.uploadImage(memberId, file);
        if (result.data) {
          setTeamMembers(teamMembers.map(m => m.id === memberId ? result.data.team_member : m));
          showToast("Team member image uploaded successfully", "success");
        }
      } else {
        // For new member, just store temporarily
        const result = await imageApi.upload(file, 'gallery');
        const newUrl = result.data?.url || result.url;
        if (newUrl) {
          setMemberFormData({ ...memberFormData, image: newUrl });
        }
      }
    } catch (error) {
      showToast("Failed to upload image", "error");
    } finally {
      setUploadingImage(false);
    }
  };

  const startEditMember = (member: any) => {
    setEditingMember(member.id);
    setMemberFormData({
      name: member.name,
      position: member.position,
      bio: member.bio || "",
      image: member.image || "",
    });
  };

  const cancelEditMember = () => {
    setEditingMember(null);
    setMemberFormData({ name: "", position: "", bio: "", image: "" });
  };

  const tabs = [
    { id: "hero" as const, label: "Hero Section", icon: LayoutTemplate },
    { id: "stats" as const, label: "Statistics", icon: BarChart3 },
    { id: "contact" as const, label: "Contact Info", icon: Mail },
    { id: "social" as const, label: "Social Links", icon: Share2 },
    { id: "footer" as const, label: "Footer", icon: FileText },
    { id: "about" as const, label: "About Page", icon: FileText },
    { id: "contactpage" as const, label: "Contact Page", icon: Mail },
    { id: "inquiry" as const, label: "Inquiry Page", icon: LayoutTemplate },
    { id: "gallerypage" as const, label: "Gallery Page", icon: ImageIcon },
    { id: "gallery" as const, label: "Photo Gallery", icon: ImageIcon },
    { id: "testimonials" as const, label: "Testimonials", icon: Quote },
    { id: "features" as const, label: "Key Features", icon: Sparkles },
    { id: "faq" as const, label: "FAQ Section", icon: HelpCircle },
    { id: "reels" as const, label: "Instagram Reels", icon: Video },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-stone-800">Frontend Settings</h1>
        <p className="text-sm text-stone-500">Manage your website content and appearance.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Sidebar Tabs */}
        <div className="w-full lg:w-64 flex-shrink-0">
          <div className="bg-white rounded-xl border border-stone-200 p-2 space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? "bg-amber-50 text-amber-700"
                      : "text-stone-600 hover:bg-stone-50"
                  }`}
                >
                  <div className={`p-1.5 rounded-lg ${activeTab === tab.id ? "bg-amber-100" : "bg-stone-100"}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1">
          {/* Hero Section */}
          {activeTab === "hero" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <LayoutTemplate className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Hero Section</h2>
                  <p className="text-sm text-stone-500">Configure the main banner on your homepage.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Hero Title
                  </label>
                  <input
                    type="text"
                    value={settings.hero_title}
                    onChange={(e) => setSettings(prev => ({ ...prev, hero_title: e.target.value }))}
                    placeholder="Enter hero title"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Hero Subtitle
                  </label>
                  <textarea
                    value={settings.hero_subtitle}
                    onChange={(e) => setSettings(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                    placeholder="Enter hero subtitle"
                    rows={3}
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Hero Images
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {settings.hero_images.map((url, index) => (
                      <div key={index} className="relative aspect-video rounded-xl overflow-hidden border border-stone-200 group">
                        <img
                          src={getImageUrl(url) || url}
                          alt={`Hero ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => handleDeleteImage(url)}
                          className="absolute top-1.5 right-1.5 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <label className="aspect-video rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors">
                      {uploadingImage ? (
                        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                      ) : (
                        <>
                          <ImageIcon className="w-6 h-6 text-stone-400 mb-1" />
                          <span className="text-xs text-stone-500">Add Image</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">Recommended size: 1920x1080px</p>
                </div>

                <div className="pt-4 border-t border-stone-100">
                  <button
                    onClick={() => handleSave({
                      hero_title: settings.hero_title,
                      hero_subtitle: settings.hero_subtitle,
                    })}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Statistics Section */}
          {activeTab === "stats" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Statistics</h2>
                  <p className="text-sm text-stone-500">Display key metrics on your homepage.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Capacity (e.g., "500+")
                  </label>
                  <input
                    type="text"
                    value={settings.stats_capacity}
                    onChange={(e) => setSettings(prev => ({ ...prev, stats_capacity: e.target.value }))}
                    placeholder="500+"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Events Hosted (e.g., "1000+")
                  </label>
                  <input
                    type="text"
                    value={settings.stats_events}
                    onChange={(e) => setSettings(prev => ({ ...prev, stats_events: e.target.value }))}
                    placeholder="1000+"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Years Experience (e.g., "15+")
                  </label>
                  <input
                    type="text"
                    value={settings.stats_experience}
                    onChange={(e) => setSettings(prev => ({ ...prev, stats_experience: e.target.value }))}
                    placeholder="15+"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Satisfaction Rate (e.g., "99%")
                  </label>
                  <input
                    type="text"
                    value={settings.stats_satisfaction}
                    onChange={(e) => setSettings(prev => ({ ...prev, stats_satisfaction: e.target.value }))}
                    placeholder="99%"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>
              </div>

              <div className="pt-4 mt-6 border-t border-stone-100">
                <button
                  onClick={() => handleSave({
                    stats_capacity: settings.stats_capacity,
                    stats_events: settings.stats_events,
                    stats_experience: settings.stats_experience,
                    stats_satisfaction: settings.stats_satisfaction,
                  })}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {/* Contact Info Section */}
          {activeTab === "contact" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Mail className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Contact Information</h2>
                  <p className="text-sm text-stone-500">Display contact details on your website.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={settings.contact_email}
                    onChange={(e) => setSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                    placeholder="contact@example.com"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={settings.contact_phone}
                    onChange={(e) => setSettings(prev => ({ ...prev, contact_phone: e.target.value }))}
                    placeholder="+91 12345 67890"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Address
                  </label>
                  <textarea
                    value={settings.contact_address}
                    onChange={(e) => setSettings(prev => ({ ...prev, contact_address: e.target.value }))}
                    placeholder="Enter your business address"
                    rows={3}
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
                  />
                </div>
              </div>

              <div className="pt-4 mt-6 border-t border-stone-100">
                <button
                  onClick={() => handleSave({
                    contact_email: settings.contact_email,
                    contact_phone: settings.contact_phone,
                    contact_address: settings.contact_address,
                  })}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {/* Social Links Section */}
          {activeTab === "social" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Share2 className="w-5 h-5 text-purple-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Social Media Links</h2>
                  <p className="text-sm text-stone-500">Connect your social media profiles.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Facebook URL
                  </label>
                  <input
                    type="url"
                    value={settings.social_facebook}
                    onChange={(e) => setSettings(prev => ({ ...prev, social_facebook: e.target.value }))}
                    placeholder="https://facebook.com/yourpage"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Instagram URL
                  </label>
                  <input
                    type="url"
                    value={settings.social_instagram}
                    onChange={(e) => setSettings(prev => ({ ...prev, social_instagram: e.target.value }))}
                    placeholder="https://instagram.com/yourpage"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Twitter/X URL
                  </label>
                  <input
                    type="url"
                    value={settings.social_twitter}
                    onChange={(e) => setSettings(prev => ({ ...prev, social_twitter: e.target.value }))}
                    placeholder="https://twitter.com/yourpage"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>
              </div>

              <div className="pt-4 mt-6 border-t border-stone-100">
                <button
                  onClick={() => handleSave({
                    social_facebook: settings.social_facebook,
                    social_instagram: settings.social_instagram,
                    social_twitter: settings.social_twitter,
                  })}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {/* Footer Section */}
          {activeTab === "footer" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-stone-100 rounded-lg">
                  <FileText className="w-5 h-5 text-stone-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Footer Content</h2>
                  <p className="text-sm text-stone-500">Configure the footer text on your website.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  About Text
                </label>
                <textarea
                  value={settings.footer_about}
                  onChange={(e) => setSettings(prev => ({ ...prev, footer_about: e.target.value }))}
                  placeholder="Enter a brief description about your business for the footer"
                  rows={6}
                  className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
                />
                <p className="text-xs text-stone-500 mt-2">
                  This text appears in the footer section of your website.
                </p>
              </div>

              <div className="pt-4 mt-6 border-t border-stone-100">
                <button
                  onClick={() => handleSave({
                    footer_about: settings.footer_about,
                  })}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {/* About Page Section */}
          {activeTab === "about" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">About Page Settings</h2>
                  <p className="text-sm text-stone-500">Configure the content for your About Us page.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Page Title</label>
                  <input
                    type="text"
                    value={settings.about_title}
                    onChange={(e) => setSettings(prev => ({ ...prev, about_title: e.target.value }))}
                    placeholder="About"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Subtitle</label>
                  <input
                    type="text"
                    value={settings.about_subtitle}
                    onChange={(e) => setSettings(prev => ({ ...prev, about_subtitle: e.target.value }))}
                    placeholder="Creating Unforgettable Memories Since 1995"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Page Content</label>
                  <textarea
                    value={settings.about_content}
                    onChange={(e) => setSettings(prev => ({ ...prev, about_content: e.target.value }))}
                    placeholder="Enter the main content for the About page..."
                    rows={8}
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
                  />
                  <p className="text-xs text-stone-500 mt-1">Supports line breaks. Each new line will be displayed as a new paragraph.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Featured Image</label>
                  <div className="grid grid-cols-1 gap-3">
                    {settings.about_image && (
                      <div className="relative aspect-video rounded-xl overflow-hidden border border-stone-200 group">
                        <img
                          src={getImageUrl(settings.about_image)}
                          alt="About"
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, about_image: "" }))}
                          className="absolute top-1.5 right-1.5 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <label className="aspect-video rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors">
                      {uploadingImage ? (
                        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                      ) : (
                        <>
                          <ImageIcon className="w-6 h-6 text-stone-400 mb-1" />
                          <span className="text-xs text-stone-500">Upload Image</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAboutImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">Recommended size: 800x600px</p>
                </div>

                {/* Team Members Section */}
                <div className="pt-6 border-t border-stone-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-md font-semibold text-stone-800">Team Members</h3>
                    {!editingMember && (
                      <button
                        onClick={() => setEditingMember(0)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600"
                      >
                        <Plus className="w-4 h-4" />
                        Add Member
                      </button>
                    )}
                  </div>
                  
                  {editingMember !== null && (
                    <div className="mb-4 p-4 border border-amber-200 rounded-xl bg-amber-50/50">
                      <h4 className="text-sm font-semibold text-stone-800 mb-3">
                        {editingMember === 0 ? 'Add New Member' : 'Edit Member'}
                      </h4>
                      <div className="space-y-3">
                        <input
                          type="text"
                          placeholder="Name"
                          value={memberFormData.name}
                          onChange={(e) => setMemberFormData({...memberFormData, name: e.target.value})}
                          className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                        />
                        <input
                          type="text"
                          placeholder="Position"
                          value={memberFormData.position}
                          onChange={(e) => setMemberFormData({...memberFormData, position: e.target.value})}
                          className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                        />
                        <textarea
                          placeholder="Bio"
                          value={memberFormData.bio}
                          onChange={(e) => setMemberFormData({...memberFormData, bio: e.target.value})}
                          rows={3}
                          className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
                        />
                        
                        {/* Image Upload for Member */}
                        <div>
                          <label className="block text-xs font-medium text-stone-700 mb-1.5">Member Photo</label>
                          <div className="flex items-center gap-3">
                            {memberFormData.image && (
                              <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-stone-200">
                                <img
                                  src={getImageUrl(memberFormData.image)}
                                  alt="Preview"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <label className="flex-1 aspect-video max-w-[200px] rounded-lg border-2 border-dashed border-stone-300 flex flex-col items-center justify-center cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors">
                              {uploadingImage ? (
                                <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                              ) : (
                                <>
                                  <ImageIcon className="w-5 h-5 text-stone-400 mb-0.5" />
                                  <span className="text-[10px] text-stone-500">Upload</span>
                                </>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleMemberImageUpload(e)}
                                className="hidden"
                                disabled={uploadingImage}
                              />
                            </label>
                          </div>
                        </div>

                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => editingMember === 0 ? handleAddTeamMember() : handleUpdateTeamMember(editingMember)}
                            className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600"
                          >
                            {editingMember === 0 ? 'Add' : 'Update'}
                          </button>
                          <button
                            onClick={cancelEditMember}
                            className="px-3 py-2 bg-stone-200 text-stone-700 rounded-lg text-sm hover:bg-stone-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {teamMembers.length === 0 && !editingMember && (
                      <p className="text-sm text-stone-500 text-center py-4">No team members yet. Click "Add Member" to get started.</p>
                    )}
                    {teamMembers.map((member) => (
                      <div key={member.id} className="p-4 border border-stone-200 rounded-xl hover:border-amber-300 transition-colors">
                        <div className="flex items-start gap-3">
                          {member.image && (
                            <img
                              src={getImageUrl(member.image)}
                              alt={member.name}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          )}
                          <div className="flex-1">
                            <h4 className="font-semibold text-stone-800">{member.name}</h4>
                            <p className="text-sm text-amber-600">{member.position}</p>
                            {member.bio && <p className="text-xs text-stone-600 mt-1 line-clamp-2">{member.bio}</p>}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => startEditMember(member)}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteTeamMember(member.id)}
                              className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-stone-100">
                  <div className="flex gap-3">
                    <button
                      onClick={loadAboutDummyData}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl font-medium text-sm hover:bg-blue-600 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Load Dummy Data
                    </button>
                    <button
                      onClick={() => handleSave({
                        about_title: settings.about_title,
                        about_subtitle: settings.about_subtitle,
                        about_content: settings.about_content,
                        about_image: settings.about_image,
                      })}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Contact Page Section */}
          {activeTab === "contactpage" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Mail className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Contact Page Settings</h2>
                  <p className="text-sm text-stone-500">Configure the Contact Us page content.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Page Title</label>
                  <input
                    type="text"
                    value={settings.contact_title}
                    onChange={(e) => setSettings(prev => ({ ...prev, contact_title: e.target.value }))}
                    placeholder="Get in Touch"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Subtitle</label>
                  <input
                    type="text"
                    value={settings.contact_subtitle}
                    onChange={(e) => setSettings(prev => ({ ...prev, contact_subtitle: e.target.value }))}
                    placeholder="We'd love to hear from you..."
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Google Map Embed Code</label>
                  <textarea
                    value={settings.contact_map_embed}
                    onChange={(e) => setSettings(prev => ({ ...prev, contact_map_embed: e.target.value }))}
                    placeholder='<iframe src="https://www.google.com/maps/embed..."'
                    rows={6}
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none font-mono"
                  />
                  <p className="text-xs text-stone-500 mt-1">Paste the full iframe embed code from Google Maps.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Contact Page Image</label>
                  <div className="grid grid-cols-1 gap-3">
                    {settings.contact_image && (
                      <div className="relative aspect-video rounded-xl overflow-hidden border border-stone-200 group">
                        <img
                          src={getImageUrl(settings.contact_image)}
                          alt="Contact"
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, contact_image: "" }))}
                          className="absolute top-1.5 right-1.5 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <label className="aspect-video rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors">
                      {uploadingImage ? (
                        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                      ) : (
                        <>
                          <ImageIcon className="w-6 h-6 text-stone-400 mb-1" />
                          <span className="text-xs text-stone-500">Upload Image</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleContactImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">Recommended size: 1920x1080px</p>
                </div>

                <div className="pt-4 border-t border-stone-100">
                  <div className="flex gap-3">
                    <button
                      onClick={loadContactDummyData}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl font-medium text-sm hover:bg-blue-600 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Load Dummy Data
                    </button>
                    <button
                      onClick={() => handleSave({
                        contact_title: settings.contact_title,
                        contact_subtitle: settings.contact_subtitle,
                        contact_map_embed: settings.contact_map_embed,
                        contact_image: settings.contact_image,
                      })}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Inquiry Page Section */}
          {activeTab === "inquiry" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <LayoutTemplate className="w-5 h-5 text-orange-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Inquiry/Book Page Settings</h2>
                  <p className="text-sm text-stone-500">Configure the event booking inquiry page.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Page Title</label>
                  <input
                    type="text"
                    value={settings.inquiry_title}
                    onChange={(e) => setSettings(prev => ({ ...prev, inquiry_title: e.target.value }))}
                    placeholder="Book Your Event"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Subtitle</label>
                  <input
                    type="text"
                    value={settings.inquiry_subtitle}
                    onChange={(e) => setSettings(prev => ({ ...prev, inquiry_subtitle: e.target.value }))}
                    placeholder="Start planning your dream celebration with us"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Description</label>
                  <textarea
                    value={settings.inquiry_description}
                    onChange={(e) => setSettings(prev => ({ ...prev, inquiry_description: e.target.value }))}
                    placeholder="Fill out the form below with your event details..."
                    rows={4}
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
                  />
                </div>

                <div className="pt-4 border-t border-stone-100">
                  <div className="flex gap-3">
                    <button
                      onClick={loadInquiryDummyData}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl font-medium text-sm hover:bg-blue-600 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Load Dummy Data
                    </button>
                    <button
                      onClick={() => handleSave({
                        inquiry_title: settings.inquiry_title,
                        inquiry_subtitle: settings.inquiry_subtitle,
                        inquiry_description: settings.inquiry_description,
                      })}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Gallery Page Settings Section */}
          {activeTab === "gallerypage" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <ImageIcon className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Gallery Page Settings</h2>
                  <p className="text-sm text-stone-500">Configure the public gallery page content and appearance.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Page Title</label>
                  <input
                    type="text"
                    value={settings.gallery_title}
                    onChange={(e) => setSettings(prev => ({ ...prev, gallery_title: e.target.value }))}
                    placeholder="Our Gallery"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">Page Subtitle</label>
                  <input
                    type="text"
                    value={settings.gallery_subtitle}
                    onChange={(e) => setSettings(prev => ({ ...prev, gallery_subtitle: e.target.value }))}
                    placeholder="Explore stunning moments captured at our venue"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">CTA Title</label>
                  <input
                    type="text"
                    value={settings.gallery_cta_title}
                    onChange={(e) => setSettings(prev => ({ ...prev, gallery_cta_title: e.target.value }))}
                    placeholder="Want to See More?"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">CTA Subtitle</label>
                  <input
                    type="text"
                    value={settings.gallery_cta_subtitle}
                    onChange={(e) => setSettings(prev => ({ ...prev, gallery_cta_subtitle: e.target.value }))}
                    placeholder="Schedule a visit to experience our venue in person"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">CTA Button Text</label>
                  <input
                    type="text"
                    value={settings.gallery_cta_button}
                    onChange={(e) => setSettings(prev => ({ ...prev, gallery_cta_button: e.target.value }))}
                    placeholder="Contact Us"
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>

                <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.gallery_show_filters}
                      onChange={(e) => setSettings(prev => ({ ...prev, gallery_show_filters: e.target.checked }))}
                      className="w-4 h-4 rounded border-stone-300 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-sm text-stone-700">Show category filters</span>
                  </label>
                </div>

                <div className="pt-4 border-t border-stone-100">
                  <button
                    onClick={() => handleSave({
                      gallery_title: settings.gallery_title,
                      gallery_subtitle: settings.gallery_subtitle,
                      gallery_cta_title: settings.gallery_cta_title,
                      gallery_cta_subtitle: settings.gallery_cta_subtitle,
                      gallery_cta_button: settings.gallery_cta_button,
                      gallery_show_filters: settings.gallery_show_filters,
                    })}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Photo Gallery Section */}
          {activeTab === "gallery" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <ImageIcon className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Photo Gallery</h2>
                  <p className="text-sm text-stone-500">Manage photo gallery albums and images</p>
                </div>
              </div>
              <GalleriesManager />
            </div>
          )}

          {/* Testimonials Section */}
          {activeTab === "testimonials" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Quote className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Client Testimonials</h2>
                  <p className="text-sm text-stone-500">Manage customer testimonials and reviews</p>
                </div>
              </div>
              <TestimonialsManager />
            </div>
          )}

          {/* Features Section */}
          {activeTab === "features" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Sparkles className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Key Features</h2>
                  <p className="text-sm text-stone-500">Manage website features and services</p>
                </div>
              </div>
              <FeaturesManager />
            </div>
          )}

          {/* FAQ Section */}
          {activeTab === "faq" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <HelpCircle className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-stone-800">FAQ Section</h2>
                    <p className="text-sm text-stone-500">Manage frequently asked questions</p>
                  </div>
                </div>
                <button
                  onClick={() => window.open('/faq', '_blank')}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Open FAQ Manager
                </button>
              </div>
              <div className="p-8 text-center bg-stone-50 rounded-xl border border-dashed border-stone-300">
                <HelpCircle className="w-12 h-12 text-stone-400 mx-auto mb-4" />
                <p className="text-stone-600 font-medium mb-2">FAQ Management</p>
                <p className="text-sm text-stone-500 mb-4">Click below to manage FAQ items in a dedicated interface</p>
                <button
                  onClick={() => window.open('/faq', '_blank')}
                  className="px-6 py-2 bg-amber-500 text-white rounded-xl font-medium text-sm hover:bg-amber-600 transition-colors"
                >
                  Manage FAQ
                </button>
              </div>
            </div>
          )}

          {/* Instagram Reels Section */}
          {activeTab === "reels" && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Video className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-800">Instagram Reels</h2>
                  <p className="text-sm text-stone-500">Manage Instagram reels and videos</p>
                </div>
              </div>
              <ReelsManager />
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
